import { useMemo, useRef, useState } from "react";
import { Plus, X, Package, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Separator } from "@/components/ui/separator";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useProductsServices } from "@/hooks/useProductsServices";
import type { QuoteLineItem } from "@/hooks/useQuickQuotes";
import { cn } from "@/lib/utils";

export interface QuickQuoteItemsTableProps {
  items: QuoteLineItem[];
  onChange: (items: QuoteLineItem[]) => void;
  discountType: "percent" | "fixed" | null;
  discountValue: number;
  onDiscountChange: (
    type: "percent" | "fixed" | null,
    value: number,
  ) => void;
}

function brl(value: number | null | undefined): string {
  if (value == null || isNaN(value)) return "R$ 0,00";
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
}

function parseCurrency(raw: string): number {
  const digits = raw.replace(/\D/g, "");
  if (!digits) return 0;
  return parseFloat(digits) / 100;
}

function newItem(partial: Partial<QuoteLineItem> = {}): QuoteLineItem {
  return {
    id: crypto.randomUUID(),
    description: "",
    quantity: 1,
    unit_price: 0,
    total: 0,
    type: "equipment",
    product_id: null,
    kit_id: null,
    ...partial,
  };
}

export function QuickQuoteItemsTable({
  items,
  onChange,
  discountType,
  discountValue,
  onDiscountChange,
}: QuickQuoteItemsTableProps) {
  const { currentTenant } = useAuth();
  const { data: products = [] } = useProductsServices();
  const [showDiscount, setShowDiscount] = useState(
    discountType !== null && discountValue > 0,
  );
  const lastInputRef = useRef<HTMLInputElement | null>(null);

  const subtotal = useMemo(
    () => items.reduce((acc, it) => acc + (it.total || 0), 0),
    [items],
  );

  const discountAmount = useMemo(() => {
    if (!discountType || !discountValue || discountValue <= 0) return 0;
    if (discountType === "percent") {
      return Math.min(subtotal, (subtotal * discountValue) / 100);
    }
    return Math.min(subtotal, discountValue);
  }, [discountType, discountValue, subtotal]);

  const total = Math.max(0, subtotal - discountAmount);

  // Listas filtradas para popovers
  const kits = useMemo(
    () => products.filter((p) => p.item_type === "kit"),
    [products],
  );
  const nonKits = useMemo(
    () => products.filter((p) => p.item_type !== "kit"),
    [products],
  );

  function updateItem(id: string, patch: Partial<QuoteLineItem>) {
    const next = items.map((it) => {
      if (it.id !== id) return it;
      const merged = { ...it, ...patch };
      merged.total =
        Number(merged.quantity || 0) * Number(merged.unit_price || 0);
      return merged;
    });
    onChange(next);
  }

  function removeItem(id: string) {
    onChange(items.filter((it) => it.id !== id));
  }

  function addEmptyItem() {
    const item = newItem();
    onChange([...items, item]);
    setTimeout(() => lastInputRef.current?.focus(), 50);
  }

  async function handleSelectKit(kitId: string, kitName: string) {
    if (!currentTenant?.id) return;
    // Buscar componentes do kit
    const { data: components, error } = await supabase
      .from("kit_components")
      .select(
        "quantity, component:products_services!kit_components_component_id_fkey(id, name, unit_price)",
      )
      .eq("kit_id", kitId)
      .eq("tenant_id", currentTenant.id);

    if (error) {
      console.warn("[QuickQuoteItemsTable] erro ao buscar componentes:", error.message);
    }

    const kitProduct = products.find((p) => p.id === kitId);
    const kitPrice = kitProduct?.unit_price ?? 0;

    const kitLine: QuoteLineItem = newItem({
      description: kitName,
      quantity: 1,
      unit_price: kitPrice,
      total: kitPrice,
      type: "kit",
      kit_id: kitId,
      product_id: kitId,
    });

    const componentLines: QuoteLineItem[] = (components || []).map((c: any) => {
      const qty = Number(c.quantity || 1);
      const price = 0; // componentes vêm sem preço unitário (preço já está no kit)
      return newItem({
        description: c.component?.name || "Componente",
        quantity: qty,
        unit_price: price,
        total: 0,
        type: "equipment",
        product_id: c.component?.id ?? null,
        parent_kit_line_id: kitLine.id,
      });
    });

    onChange([...items, kitLine, ...componentLines]);
  }

  return (
    <div className="space-y-4">
      {/* Top bar: botão Usar kit */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Popover>
          <PopoverTrigger asChild>
            <Button type="button" size="sm" variant="outline">
              <Package className="h-3.5 w-3.5 mr-1.5" />
              Usar kit
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-0" align="start">
            <div className="p-2 border-b text-xs text-muted-foreground">
              Selecione um kit do catálogo
            </div>
            <Command shouldFilter={false}>
              <CommandList className="max-h-72">
                <CommandEmpty>Nenhum kit cadastrado.</CommandEmpty>
                <CommandGroup>
                  {kits.map((k) => (
                    <CommandItem
                      key={k.id}
                      value={k.id}
                      onSelect={() => handleSelectKit(k.id, k.name)}
                      className="flex flex-col items-start gap-0.5 cursor-pointer"
                    >
                      <div className="font-medium truncate w-full">{k.name}</div>
                      <div className="text-xs text-muted-foreground flex items-center gap-2">
                        {k.code && <span>{k.code}</span>}
                        <span>{brl(k.unit_price)}</span>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <div className="text-xs text-muted-foreground">
          {items.length} {items.length === 1 ? "item" : "itens"}
        </div>
      </div>

      {/* Header desktop */}
      <div className="hidden md:grid grid-cols-[1fr_80px_120px_120px_36px] gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground px-1">
        <div>Descrição</div>
        <div className="text-center">Qtd</div>
        <div className="text-right">Preço/un</div>
        <div className="text-right">Total</div>
        <div />
      </div>

      {/* Lines */}
      <div className="space-y-2">
        {items.length === 0 && (
          <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
            Nenhum item adicionado.
            <br />
            Clique em <span className="font-medium">+ Adicionar equipamento</span>{" "}
            ou em <span className="font-medium">Usar kit</span>.
          </div>
        )}
        {items.map((item, idx) => (
          <div
            key={item.id}
            className={cn(
              "grid gap-2 p-2 rounded-lg border bg-card",
              "grid-cols-1 md:grid-cols-[1fr_80px_120px_120px_36px]",
            )}
          >
            {/* Descrição */}
            <div className="min-w-0">
              <ProductCombobox
                value={item.description}
                products={nonKits}
                inputRef={idx === items.length - 1 ? lastInputRef : undefined}
                onSelect={(p) =>
                  updateItem(item.id, {
                    description: p.name,
                    product_id: p.id,
                    unit_price: p.unit_price ?? 0,
                  })
                }
                onChange={(text) =>
                  updateItem(item.id, {
                    description: text,
                    product_id: text === item.description ? item.product_id : null,
                  })
                }
              />
            </div>

            {/* Qtd */}
            <div>
              <Input
                type="number"
                min={1}
                step={1}
                value={item.quantity}
                onChange={(e) =>
                  updateItem(item.id, {
                    quantity: Math.max(1, Number(e.target.value) || 1),
                  })
                }
                className="text-center md:text-center"
              />
            </div>

            {/* Preço unitário */}
            <div>
              <Input
                inputMode="numeric"
                value={
                  item.unit_price > 0 ? brl(item.unit_price) : ""
                }
                placeholder="R$ 0,00"
                onChange={(e) =>
                  updateItem(item.id, {
                    unit_price: parseCurrency(e.target.value),
                  })
                }
                className="text-right"
              />
            </div>

            {/* Total */}
            <div className="flex items-center justify-end pr-1 text-sm font-semibold tabular-nums">
              {brl(item.total)}
            </div>

            {/* Remover */}
            <div className="flex items-center justify-center">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-muted-foreground hover:text-destructive"
                onClick={() => removeItem(item.id)}
                aria-label="Remover item"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={addEmptyItem}
        className="w-full"
      >
        <Plus className="h-4 w-4 mr-1.5" />
        Adicionar equipamento
      </Button>

      {/* Totais */}
      {items.length > 0 && (
        <>
          <Separator />
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Subtotal</span>
              <span className="font-semibold tabular-nums">{brl(subtotal)}</span>
            </div>

            {!showDiscount && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowDiscount(true);
                  onDiscountChange("percent", 0);
                }}
                className="text-xs h-7 px-2"
              >
                <Plus className="h-3 w-3 mr-1" />
                Desconto
              </Button>
            )}

            {showDiscount && (
              <div className="rounded-lg border bg-muted/30 p-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1">
                    <Button
                      type="button"
                      variant={discountType === "percent" ? "default" : "outline"}
                      size="sm"
                      className="h-7 px-2 text-xs"
                      onClick={() =>
                        onDiscountChange("percent", discountValue)
                      }
                    >
                      %
                    </Button>
                    <Button
                      type="button"
                      variant={discountType === "fixed" ? "default" : "outline"}
                      size="sm"
                      className="h-7 px-2 text-xs"
                      onClick={() =>
                        onDiscountChange("fixed", discountValue)
                      }
                    >
                      R$
                    </Button>
                  </div>
                  <Input
                    inputMode="numeric"
                    value={
                      discountType === "fixed"
                        ? discountValue > 0
                          ? brl(discountValue)
                          : ""
                        : discountValue > 0
                          ? String(discountValue)
                          : ""
                    }
                    placeholder={discountType === "percent" ? "0" : "R$ 0,00"}
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (discountType === "fixed") {
                        onDiscountChange("fixed", parseCurrency(raw));
                      } else {
                        const num = Math.max(0, Math.min(100, Number(raw) || 0));
                        onDiscountChange("percent", num);
                      }
                    }}
                    className="h-8 max-w-[120px] text-right"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground"
                    onClick={() => {
                      setShowDiscount(false);
                      onDiscountChange(null, 0);
                    }}
                    aria-label="Remover desconto"
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
                {discountAmount > 0 && (
                  <div className="flex items-center justify-between text-sm text-destructive">
                    <span>Desconto aplicado</span>
                    <span className="font-semibold tabular-nums">
                      − {brl(discountAmount)}
                    </span>
                  </div>
                )}
              </div>
            )}

            <Separator />
            <div className="flex items-center justify-between">
              <span className="font-semibold">Total</span>
              <span className="text-lg font-bold tabular-nums">{brl(total)}</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─────────────── ProductCombobox ───────────────

interface ProductComboboxProps {
  value: string;
  products: Array<{
    id: string;
    name: string;
    code: string | null;
    unit_price: number;
  }>;
  onSelect: (p: { id: string; name: string; unit_price: number }) => void;
  onChange: (text: string) => void;
  inputRef?: React.MutableRefObject<HTMLInputElement | null>;
}

function ProductCombobox({
  value,
  products,
  onSelect,
  onChange,
  inputRef,
}: ProductComboboxProps) {
  const [open, setOpen] = useState(false);
  const filtered = useMemo(() => {
    const q = (value || "").trim().toLowerCase();
    if (!q) return products.slice(0, 50);
    return products
      .filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          (p.code || "").toLowerCase().includes(q),
      )
      .slice(0, 50);
  }, [value, products]);

  return (
    <Popover open={open && filtered.length > 0} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div className="relative">
          <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
          <Input
            ref={inputRef}
            value={value}
            onChange={(e) => {
              onChange(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            placeholder="Buscar produto ou digitar..."
            className="pl-7"
          />
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 w-[--radix-popover-trigger-width] max-w-sm"
        align="start"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <Command shouldFilter={false}>
          <div className="max-h-60 overflow-y-auto overscroll-contain">
            <CommandEmpty>Nenhum produto encontrado.</CommandEmpty>
            <CommandGroup>
              {filtered.map((p) => (
                <CommandItem
                  key={p.id}
                  value={p.id}
                  onSelect={() => {
                    onSelect({ id: p.id, name: p.name, unit_price: p.unit_price });
                    setOpen(false);
                  }}
                  className="flex flex-col items-start gap-0.5 cursor-pointer"
                >
                  <div className="truncate font-medium w-full">{p.name}</div>
                  <div className="text-xs text-muted-foreground flex justify-between w-full">
                    <span>{p.code || ""}</span>
                    <span>{brl(p.unit_price)}</span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </div>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
